export * from './navSections';
export * from './navSectionLabels';
export * from './navSectionPermissions';

const toBit = (n: number) => 1 << n;

/**
 * !! IF WE WANT TO KEEP BITMAP SEMANTICS "ALL" MUST BE LIMITED TO 30 BITS AND ALL OTHERS SHOULD BE BELOW THAT !!
 */
export const fullPermissionMask = toBit(30) - 1; // using 30 bits for permissions

export const Permissions = {
  documents: {
    view: toBit(0),
    create: toBit(1),
    update: toBit(2),
    delete: toBit(3)
  },
  connection: {
    add: toBit(0),
    edit: toBit(1),
    delete: toBit(2)
  },
  connection_asset: {
    create: toBit(0),
    edit: toBit(1),
    delete: toBit(2)
  },
  connection_asset_measure: {
    view: toBit(0),
    create: toBit(1),
    update: toBit(2),
    delete: toBit(3)
  },
  bia: {
    list: toBit(0),
    view: toBit(1),
    create: toBit(2),
    update: toBit(3),
    delete: toBit(4)
  },
  asset: {
    list: toBit(0),
    view: toBit(1),
    create: toBit(2),
    update: toBit(3),
    export: toBit(4)
  },
  asset_owner: { update: toBit(0) },
  user: {
    list: toBit(0),
    view: toBit(1),
    invite: toBit(2),
    update: toBit(3),
    export: toBit(4),
    delete: toBit(5)
  },
  user_roles: {
    add: toBit(0),
    delete: toBit(1)
  },
  risk: {
    list: toBit(0),
    view: toBit(1),
    create: toBit(2),
    update: toBit(3)
  },
  risk_owner: { update: toBit(0) },
  risk_assessor: { update: toBit(0) },
  risk_committee: { update: toBit(0) },
  risk_asset: { update: toBit(0) },
  risk_threat: { update: toBit(0) },
  risk_vulnerability: { update: toBit(0) },
  risk_measure: { update: toBit(0) },
  threat: {
    list: toBit(0),
    view: toBit(1),
    create: toBit(2),
    update: toBit(3)
  },
  measure: {
    list: toBit(0),
    view: toBit(1),
    create: toBit(2),
    update: toBit(3)
  },
  vulnerability: {
    list: toBit(0),
    view: toBit(1),
    create: toBit(2)
  },
  kpi: {
    list: toBit(0),
    view: toBit(1),
    create: toBit(2),
    update: toBit(3)
  },
  kpi_values: {
    create: toBit(0),
    update: toBit(1)
  },
  eventsAndIncidents: {
    list: toBit(0),
    view: toBit(1),
    create: toBit(2),
    update: toBit(3),
    close: toBit(4)
  },
  disasterRecovery: {
    list: toBit(0),
    view: toBit(1),
    create: toBit(2),
    update: toBit(3),
    delete: toBit(4),
    test_schedule: toBit(5),
    test_run: toBit(6),
    test_close: toBit(7),
    evidence_attach: toBit(8)
  },
  settings_organisation: {
    view: toBit(0),
    update: toBit(1)
  },
  settings_bia: {
    view: toBit(0),
    update: toBit(1)
  },
  settings_corporateTree: {
    view: toBit(0),
    create: toBit(1),
    update: toBit(2)
  },
  settings_functions: {
    view: toBit(0)
  },
  settings_frameworks: {
    view: toBit(0),
    update: toBit(1)
  },
  settings_ssoAndProvisioning: {
    view: toBit(0)
  },
  settings_RolesAndPermissions: {
    view: toBit(0)
  },
  settings_RolesAndPermissions_organisationRoles: {
    view: toBit(0),
    create: toBit(1),
    update: toBit(2)
  },
  settings_RolesAndPermissions_specialGroups: {
    view: toBit(0),
    create: toBit(1),
    update: toBit(2)
  },
  settings_teamMembers: {
    view: toBit(0),
    create: toBit(1),
    update: toBit(2),
    invite: toBit(3),
    delete: toBit(4),
    owner: toBit(5)
  },
  settings_invites: {
    view: toBit(0),
    create: toBit(1)
  },
  organisation_context: {
    view: toBit(0),
    create: toBit(1)
  },
  assessments: {
    list: toBit(0),
    view: toBit(1),
    create: toBit(2),
    update: toBit(3),
    delete: toBit(4)
  },
  // gen360 marketplace section. `view` is broad — anyone in the org
  // can browse the catalog. `purchase` and `redeem` gate the
  // money-spending + voucher-redeem actions to designated billing
  // owners. `approve` gates the requiresApproval review flow, which
  // happens locally on the asign side (gen360 only emits the flag).
  // `manage` covers connection pairing/rotation by the saas admin.
  gen360_marketplace: {
    view: toBit(0),
    purchase: toBit(1),
    redeem: toBit(2),
    approve: toBit(3),
    manage: toBit(4)
  },
  // Automated access matrix validation. `view` lets users see drift
  // findings (read-only). `configure` covers creating / editing /
  // pausing checks. `run` lets a user manually trigger a re-validation
  // outside the schedule. `resolve` is needed to acknowledge / resolve
  // / ignore individual findings. Audit / IT-Sec roles typically get
  // all four; ordinary org members see nothing.
  access_validation: {
    view: toBit(0),
    configure: toBit(1),
    run: toBit(2),
    resolve: toBit(3)
  },
  // Settings import / export. `view` exposes the page + history.
  // `export` lets the user download a settings bundle (read-only —
  // no data mutation). `import` allows uploading + applying a bundle
  // (writes across many sections, so this is the most privileged
  // bit; usually reserved for org owners + dedicated admins).
  import_export: {
    view: toBit(0),
    export: toBit(1),
    import: toBit(2)
  },
  // The sections below are stored as `Asset` rows for persistence but are
  // distinct product surfaces, so each carries its own ACL instead of the
  // shared `asset` one. Action set mirrors `asset` (list/view/create/
  // update/export). See `assetPermissionSection` for the type→section map.
  goal: {
    list: toBit(0),
    view: toBit(1),
    create: toBit(2),
    update: toBit(3),
    export: toBit(4)
  },
  securityProject: {
    list: toBit(0),
    view: toBit(1),
    create: toBit(2),
    update: toBit(3),
    export: toBit(4)
  },
  exception: {
    list: toBit(0),
    view: toBit(1),
    create: toBit(2),
    update: toBit(3),
    export: toBit(4),
    approve: toBit(5),
    activate: toBit(6)
  },
  incidentScenario: {
    list: toBit(0),
    view: toBit(1),
    create: toBit(2),
    update: toBit(3),
    export: toBit(4)
  },
  playbook: {
    list: toBit(0),
    view: toBit(1),
    create: toBit(2),
    update: toBit(3),
    export: toBit(4)
  },
  policy: {
    list: toBit(0),
    view: toBit(1),
    create: toBit(2),
    update: toBit(3),
    export: toBit(4)
  },
  video: {
    list: toBit(0),
    view: toBit(1),
    create: toBit(2),
    update: toBit(3),
    export: toBit(4)
  },
  campaign: {
    list: toBit(0),
    view: toBit(1),
    create: toBit(2),
    update: toBit(3),
    export: toBit(4)
  },
  // Time tracking (clockify-style). Currently FE-only; these bits are
  // declared ahead of the backend so the section already appears in the
  // admin ACL UI and roles can be granted it. When the time* resolvers
  // get guarded, they read these bits — no further ACL plumbing needed.
  //
  // The first five bits are ALWAYS self-scoped — they only ever act on
  // the calling user's own data, so the resolver doesn't need to inspect
  // who owns the row:
  //   - `view`          see your own timesheet
  //   - `create`        log your own entries (incl. start / stop timer)
  //   - `update`        edit your own entries
  //   - `delete`        delete your own entries
  //   - `timeOffManage` request / manage your own holidays + time off
  //
  // `admin` is the single cross-user gate. It unlocks everything beyond
  // your own data: viewing and editing ANYONE's entries, managing
  // time-tracking projects, the org-wide reports, and exports. A resolver
  // that touches another user's data (or the whole org) checks `admin`;
  // a resolver scoped to the caller checks the matching self bit above.
  timetracking: {
    view: toBit(0),
    create: toBit(1),
    update: toBit(2),
    delete: toBit(3),
    timeOffManage: toBit(4),
    admin: toBit(5)
  }
} as const;

type Section = Extract<keyof typeof Permissions, string>;
type Action<S extends Section> = Extract<keyof (typeof Permissions)[S], string>;
type PermissionString = {
  [S in Section]: `${S}.${Action<S>}` | `${S}.*`;
}[Section];
export type PermissionInput = PermissionString | PermissionString[];

export type ACL = {
  [key in Section]?: number | '*'; // "*" = full access
};

/**
 * Some `Asset` types are distinct product surfaces with their own ACL
 * section (they're stored as assets only for persistence). Map an asset
 * `typeId` to the section that gates it. Everything not listed — the
 * asset register itself (business services / functions / enabling
 * services / assets), people (customers / suppliers / interested
 * parties), and the asset taxonomy / responsibility views (RASCI,
 * classifications) — stays on the shared `asset` section.
 */
export const assetTypePermissionSection: Record<string, Section> = {
  goals: 'goal',
  'security-project': 'securityProject',
  exceptions: 'exception',
  scenarios: 'incidentScenario',
  playbook: 'playbook',
  policy: 'policy',
  video: 'video',
  campaign: 'campaign'
};

/**
 * Resolve the permission section that gates an asset of the given type.
 * Falls back to `asset` for any type without a dedicated section.
 */
export function assetPermissionSection(typeId: string | null | undefined): Section {
  if (typeId == null) return 'asset';
  return assetTypePermissionSection[typeId] ?? 'asset';
}

/**
 * Build the permission string gating a given action on an asset of the
 * given type, routed through `assetPermissionSection`. Every section it
 * can resolve to (asset + the carved-out sections) shares the same
 * action set, so the cast is always valid.
 */
export function assetPermission(
  typeId: string | null | undefined,
  action: 'list' | 'view' | 'create' | 'update' | 'export'
): PermissionInput {
  return `${assetPermissionSection(typeId)}.${action}` as PermissionInput;
}

/**
 * Convert wildcard '*' to full bitmask
 */
function resolveAcl(acl: number | '*'): number {
  if (acl === '*') return fullPermissionMask;
  return acl;
}

/**
 * Compute computed ACL: org ACL takes precedence, user cannot exceed org
 */
export function getResolvedPermissions({
  organisationPermissions,
  userPermissions: userAcl
}: {
  organisationPermissions: ACL;
  userPermissions: ACL;
}): ACL {
  const resolved: ACL = {};

  for (const section of Object.keys(Permissions) as Section[]) {
    const orgBits = organisationPermissions[section]
      ? resolveAcl(organisationPermissions[section]!)
      : 0;
    const userBits = userAcl[section] ? resolveAcl(userAcl[section]!) : 0;

    resolved[section] = orgBits & userBits; // intersection
  }

  return resolved;
}

/**
 * Clamp a child organisation ACL by its ancestor chain so a child can
 * never hold more permission bits than its parent. `chain` is ordered
 * root-first → child-last; only the ancestors (everything except the
 * last entry) are used for clamping. Returns the child ACL with every
 * section AND-ed against the intersection of its ancestors.
 *
 * Used by the admin UI / mutation so a child company's stored ACL is
 * narrowed when its parent's permissions are reduced — otherwise the
 * waterfall would only hold at read-time via `getResolvedPermissions`
 * and the stored bits would silently disagree with the effective ones.
 */
export function clampAclByAncestors({
  childAcl,
  ancestorAcls
}: {
  childAcl: ACL;
  ancestorAcls: readonly ACL[];
}): ACL {
  if (ancestorAcls.length === 0) return childAcl;
  const ancestorIntersection: Record<string, number> = {};
  for (const section of Object.keys(Permissions) as Section[]) {
    let bits = fullPermissionMask;
    for (const ancestor of ancestorAcls) {
      const value = ancestor[section];
      const resolved = value == null ? 0 : resolveAcl(value);
      bits &= resolved;
    }
    ancestorIntersection[section] = bits;
  }
  const out: ACL = {};
  for (const section of Object.keys(Permissions) as Section[]) {
    const childValue = childAcl[section];
    if (childValue == null) continue;
    const childBits = resolveAcl(childValue);
    const clamped = childBits & ancestorIntersection[section];
    if (clamped !== 0) out[section] = clamped;
  }
  return out;
}

/**
 * Compute resolved permissions from GraphQL ACL arrays
 */
export function getResolvedPermissionsFromGraphql(
  organisationPermissions: Array<{ section: string; value: number }>,
  userAcl: Array<{ section: string; value: number }>
): ACL {
  return getResolvedPermissions({
    organisationPermissions: organisationPermissions.reduce((acc, item) => {
      acc[item.section as Section] = item.value;
      return acc;
    }, {} as ACL),
    userPermissions: userAcl.reduce((acc, item) => {
      acc[item.section as Section] = item.value;
      return acc;
    }, {} as ACL)
  });
}

/**
 * Parse permission string into section and mask
 */
function parsePermission(input: PermissionString): {
  section: Section;
  mask: number;
} {
  const [sectionRaw, action] = input.split('.') as [Section, string];

  const sectionPermissions = Permissions[sectionRaw];
  if (!sectionPermissions) {
    throw new Error(`Unknown section: ${sectionRaw}`);
  }

  if (action === '*') {
    return {
      section: sectionRaw,
      mask: fullPermissionMask
    };
  }

  const permission = (sectionPermissions as any)[action];
  if (typeof permission !== 'number') {
    throw new Error(`Unknown permission: ${input}`);
  }

  return {
    section: sectionRaw,
    mask: permission
  };
}

/**
 * Check if ACL has required permission(s)
 */
export function hasPermission(acl: ACL, input: PermissionInput): boolean {
  const permissions = Array.isArray(input) ? input : [input];

  // group permissions per section
  const requiredBySection: Partial<Record<Section, number>> = {};

  for (const p of permissions) {
    const { section, mask } = parsePermission(p);
    requiredBySection[section] = (requiredBySection[section] ?? 0) | mask;
  }

  // check per section
  for (const [section, required] of Object.entries(requiredBySection)) {
    const sectionAcl = acl[section as Section];
    if (!sectionAcl) return false;

    const resolved = resolveAcl(sectionAcl);

    if ((resolved & required) !== required) return false;
  }

  return true;
}

/**
 * Set or remove permission(s) for one or multiple sections
 * @param acl - ACL object to modify
 * @param updates - object with section keys and permissions to set/remove
 *                  e.g., { asset: 1, risk: "*" }
 * @param enable - true = set/add, false = remove
 */
export function setPermissions(
  acl: ACL,
  updates: Partial<{ [S in Section]: number | number[] | '*' }>,
  enable = true
): ACL {
  const newAcl = { ...acl };

  for (const sectionKey of Object.keys(updates) as Section[]) {
    const sectionUpdate = updates[sectionKey];
    if (!sectionUpdate) continue;

    // Current ACL bits
    let currentBits = resolveAcl(newAcl[sectionKey] ?? 0);

    if (sectionUpdate === '*') {
      currentBits = enable ? fullPermissionMask : 0;
    } else {
      const perms = Array.isArray(sectionUpdate) ? sectionUpdate : [sectionUpdate];
      currentBits = perms.reduce((acc, p) => {
        return enable ? acc | p : acc & ~p;
      }, currentBits);
    }

    newAcl[sectionKey] = currentBits;
  }

  return newAcl;
}

/**
 * Convert numeric ACL for a section to array of permission keys
 */
export function decodePermissions(section: keyof typeof Permissions, acl: number): string[] {
  const perms = Permissions[section];
  const result: string[] = [];

  for (const [key, bit] of Object.entries(perms)) {
    if (key === 'all') continue; // skip "all" helper
    if ((acl & bit) === bit) {
      result.push(key);
    }
  }

  return result;
}

/**
 * Merge multiple permission sets into one
 */
export function mergePermissionSets(
  permissionSets: Array<Record<string, number>>
): Record<string, number> {
  const merged: Record<string, number> = {};

  for (const set of permissionSets) {
    for (const [section, value] of Object.entries(set)) {
      merged[section] = (merged[section] || 0) | value;
    }
  }

  return merged;
}

/**
 * Parse permissions object into array for GraphQL
 */
export function parsePermissions(permissions: any): Array<{ section: string; value: number }> {
  if (permissions == null || typeof permissions !== 'object') return [];

  const aclArray = Object.entries(permissions).map(([section, value]) => ({
    section,
    value: Number(value)
  }));

  return aclArray;
}

export type EntityPermission = {
  id: string;

  entityType: string;
  entityId: string;

  permissions: ACL;

  type: 'unlimited' | 'timeBased' | 'oneOff';

  validFrom?: Date;
  validTo?: Date;

  used: boolean;
  usedAt?: Date;

  userId: string;
};

/**
 * Check if entity permission is active
 */
function isEntityPermissionActive(ep: EntityPermission, now = new Date()): boolean {
  if (ep.type === 'oneOff') {
    return !ep.used;
  }

  if (ep.type === 'timeBased') {
    if (!ep.validFrom || !ep.validTo) return false;
    return ep.validFrom <= now && ep.validTo >= now;
  }

  // unlimited
  return true;
}

/**
 * Resolve entity-level ACL for a specific entity
 * - merges all active EntityPermissions
 * - respects time / oneOff / unlimited
 * - converts "*" to full bitmask
 */
export function resolveEntityAcl(
  entityPermissions: EntityPermission[],
  entityType: string,
  entityId: string,
  now = new Date()
): ACL {
  const resolved: ACL = {};

  for (const ep of entityPermissions) {
    // Scope check
    if (ep.entityType !== entityType || ep.entityId !== entityId) {
      continue;
    }

    // Lifecycle check
    if (!isEntityPermissionActive(ep, now)) {
      continue;
    }

    // Merge permissions
    for (const section of Object.keys(ep.permissions) as Section[]) {
      const value = ep.permissions[section];
      if (!value) continue;

      const mask = value === '*' ? fullPermissionMask : resolveAcl(value);

      const current = resolveAcl(resolved[section] ?? 0);
      resolved[section] = current | mask;
    }
  }

  return resolved;
}

/**
 * Check if a user has a specific permission on a given entity
 */
export function hasPermissionToEntity(params: {
  acl: ACL;
  entityPermissions: EntityPermission[];
  entityType: string;
  entityId: string;
  input: PermissionInput;
  now?: Date;
}): boolean {
  const { acl, entityPermissions, entityType, entityId, input, now = new Date() } = params;

  const permissions = Array.isArray(input) ? input : [input];

  if (!hasPermission(acl, permissions)) {
    return false;
  }

  const entityAcl = resolveEntityAcl(entityPermissions, entityType, entityId, now);

  if (!Object.keys(entityAcl).length) {
    return false;
  }

  return hasPermission(entityAcl, permissions);
}
